/**
 * Pure helpers for encoding / parsing turn-failure markers.
 * SAFE TO IMPORT FROM CLIENT — no DB deps. Keep it that way.
 */
import type { UIMessage } from 'ai';

/** Unique, stable prefix for detecting a turn-error text part. */
export const TURN_ERROR_MARKER = '__GALEXIA_TURN_ERROR__';

export type TurnErrorKind = 'cancelled' | 'timeout' | 'error';

export interface TurnErrorPayload {
  kind: TurnErrorKind;
  message: string;
}

/** Serialize an error payload into a text part's `text` field.
 *  Format: `${MARKER}\n${JSON payload}`. */
export function encodeTurnError(payload: TurnErrorPayload): string {
  return `${TURN_ERROR_MARKER}\n${JSON.stringify(payload)}`;
}

/** Inverse of encodeTurnError. Returns null if the text is not a turn-error marker. */
export function parseTurnError(text: string): TurnErrorPayload | null {
  if (!text.startsWith(TURN_ERROR_MARKER)) return null;
  try {
    const jsonLine = text.slice(TURN_ERROR_MARKER.length).trim();
    return JSON.parse(jsonLine) as TurnErrorPayload;
  } catch {
    return null;
  }
}

export function isTurnErrorText(text: string): boolean {
  return parseTurnError(text) !== null;
}

export function hasTurnErrorMarker(message: Pick<UIMessage, 'parts'>): boolean {
  return message.parts.some((part) => part.type === 'text' && isTurnErrorText(part.text));
}

/**
 * Remove retry/failure marker text parts before sending a continuation retry.
 *
 * The marker is UI state, not useful model context. Keeping it in the prompt makes
 * the next model see a synthetic assistant error as if it were generated content.
 */
export function stripTurnErrorMarkersFromMessages<T extends UIMessage>(messages: T[]): T[] {
  return messages.flatMap((message) => {
    if (!hasTurnErrorMarker(message)) return [message];

    const parts = message.parts.filter(
      (part) => !(part.type === 'text' && isTurnErrorText(part.text)),
    );

    if (parts.length === 0) return [];

    const metadata = message.metadata;
    const cleanedMetadata =
      metadata && typeof metadata === 'object' && !Array.isArray(metadata)
        ? Object.fromEntries(
            Object.entries(metadata as Record<string, unknown>).filter(([key]) => key !== 'turnFailed'),
          )
        : metadata;

    return [{
      ...message,
      parts,
      ...(cleanedMetadata === undefined ? {} : { metadata: cleanedMetadata }),
    } as T];
  });
}
